import { ENV } from '../../config/runtimeEnv';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

export interface EmailSignature {
	id: number;
	company_id: number;
	name: string;
	content: string;
	is_default: boolean;
	created_by_user_id?: UserUuid;
	created_at: string;
	updated_at: string;
}

export interface CreateEmailSignatureRequest {
	company_id: number;
	name: string;
	content: string;
	is_default?: boolean;
	created_by_user_id?: UserUuid;
}

export interface UpdateEmailSignatureRequest {
	name?: string;
	content?: string;
	is_default?: boolean;
}

const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

export const fetchEmailSignatures = async (companyId: number): Promise<EmailSignature[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<EmailSignature[]>('/email-signatures', {
			params: { company_id: companyId },
		});
		return response.data;
	} catch (error) {
		console.error('Error fetching email signatures:', error);
		throw error;
	}
};

export const fetchEmailSignature = async (id: number): Promise<EmailSignature> => {
	try {
		const api = createApiInstance();
		const response = await api.get<EmailSignature>(`/email-signatures/${id}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching email signature:', error);
		throw error;
	}
};

export const fetchDefaultSignature = async (companyId: number): Promise<EmailSignature | null> => {
	try {
		const api = createApiInstance();
		const response = await api.get<EmailSignature>('/email-signatures/default', {
			params: { company_id: companyId },
		});
		return response.data;
	} catch (error) {
		console.error('Error fetching default signature:', error);
		return null;
	}
};

export const createEmailSignature = async (data: CreateEmailSignatureRequest): Promise<EmailSignature> => {
	try {
		const api = createApiInstance();
		const response = await api.post<EmailSignature>('/email-signatures', data);
		return response.data;
	} catch (error) {
		console.error('Error creating email signature:', error);
		throw error;
	}
};

export const updateEmailSignature = async (id: number, data: UpdateEmailSignatureRequest): Promise<EmailSignature> => {
	try {
		const api = createApiInstance();
		const response = await api.put<EmailSignature>(`/email-signatures/${id}`, data);
		return response.data;
	} catch (error) {
		console.error('Error updating email signature:', error);
		throw error;
	}
};

export const deleteEmailSignature = async (id: number): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.delete(`/email-signatures/${id}`);
	} catch (error) {
		console.error('Error deleting email signature:', error);
		throw error;
	}
};

export const setDefaultSignature = async (id: number, companyId: number): Promise<EmailSignature> => {
	try {
		const api = createApiInstance();
		const response = await api.put<EmailSignature>(`/email-signatures/${id}/set-default`, {
			company_id: companyId,
		});
		return response.data;
	} catch (error) {
		console.error('Error setting default signature:', error);
		throw error;
	}
};